import React from 'react'
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Avatar,
} from '@mui/material'
import {
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { formatDate, formatDistanceToNow, formatCredits } from '../../utils/formatters'

const TransactionDataGrid = ({
  transactions = [],
  isLoading = false,
  error = null,
  onViewDetails,
  pagination = { total: 0, page: 1, limit: 25 },
  getTypeChip,
  getStatusChip,
}) => {

  const getTransactionIcon = (type, amount) => {
    const iconProps = { sx: { fontSize: 20 } }
    
    switch (type) {
      case 'purchase':
        return <TrendingUpIcon color="success" {...iconProps} />
      case 'rental':
        return <TrendingDownIcon color="primary" {...iconProps} />
      case 'admin_adjustment':
        return amount > 0 ? 
          <TrendingUpIcon color="warning" {...iconProps} /> : 
          <TrendingDownIcon color="warning" {...iconProps} />
      default:
        return <ReceiptIcon color="action" {...iconProps} />
    }
  }

  const getUserAvatar = (email) => {
    if (!email) return null
    const initials = email.substring(0, 2).toUpperCase()
    return (
      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
        {initials}
      </Avatar>
    )
  }

  const columns = [
    {
      field: 'transaction',
      headerName: 'İşlem',
      flex: 1,
      minWidth: 280,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          {getTransactionIcon(params.row.type, params.row.amount_in_credits)}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {getTypeChip(params.row.type)}
              {getStatusChip(params.row.status)}
            </Box>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.id.slice(0, 8)}...
              {params.row.external_transaction_id && (
                <> • Ext: {params.row.external_transaction_id}</>
              )}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'user',
      headerName: 'Kullanıcı',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getUserAvatar(params.row.user_email)}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {params.row.user_email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.user_id.slice(0, 8)}...
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'amount_in_credits',
      headerName: 'Kredi Miktarı',
      width: 130,
      renderCell: (params) => {
        const amount = params.value
        const isPositive = amount > 0
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon 
              color={isPositive ? 'success' : 'error'} 
              sx={{ fontSize: 16 }} 
            />
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: isPositive ? 'success.main' : 'error.main'
              }}
            >
              {isPositive ? '+' : ''}{amount}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'amount_usd',
      headerName: 'USD Değeri',
      width: 120,
      renderCell: (params) => {
        const amount = params.value
        if (!amount) return '-'
        const isPositive = amount > 0
        return (
          <Typography 
            variant="body2" 
            color={isPositive ? 'success.main' : 'error.main'}
            sx={{ fontWeight: 500 }}
          >
            {isPositive ? '+' : ''}${Math.abs(amount)}
          </Typography>
        )
      },
    },
    {
      field: 'created_at',
      headerName: 'Tarih',
      width: 160,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {formatDate(params.value, 'dd/MM/yyyy')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(params.value, 'HH:mm:ss')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'timeAgo',
      headerName: 'Ne Kadar Önce',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatDistanceToNow(params.row.created_at)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Detayları Görüntüle">
            <IconButton
              size="small"
              onClick={() => onViewDetails && onViewDetails(params.row)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        İşlemler yüklenirken hata oluştu: {error.message}
      </Alert>
    )
  }

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={transactions}
        columns={columns}
        loading={isLoading}
        pagination
        page={pagination.page - 1} // DataGrid uses 0-based indexing
        pageSize={pagination.limit}
        rowCount={pagination.total}
        paginationMode="server"
        disableSelectionOnClick
        getRowHeight={() => 70}
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'grey.50',
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />
    </Box>
  )
}

export default TransactionDataGrid